/**
 * ReminderTimePicker Component
 * 
 * Allows users to select custom reminder times for appointments.
 * Supports both preset options and custom time selection.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bell, Plus, X, Clock } from 'lucide-react';

// Preset reminder options (in minutes)
export const PRESET_REMINDERS = [
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 240, label: '4 hours before' },
  { value: 480, label: '8 hours before' },
  { value: 1440, label: '1 day before' },
  { value: 2880, label: '2 days before' },
  { value: 10080, label: '1 week before' },
];

// Default reminders
export const DEFAULT_REMINDERS = [1440, 120, 30]; // 1 day, 2 hours, 30 minutes

/**
 * Format minutes to human-readable string
 */
export const formatReminderTime = (minutes) => {
  if (minutes >= 10080) {
    const weeks = Math.floor(minutes / 10080);
    return `${weeks} week${weeks > 1 ? 's' : ''} before`;
  }
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    return `${days} day${days > 1 ? 's' : ''} before`;
  }
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} before`;
  }
  return `${minutes} minute${minutes > 1 ? 's' : ''} before`;
};

/**
 * ReminderTimePicker Dialog Component
 */
const ReminderTimePicker = ({
  isOpen,
  onClose,
  selectedTimes = DEFAULT_REMINDERS,
  onSave,
  title = 'Set Reminder Times',
}) => {
  const [selected, setSelected] = useState(new Set(selectedTimes));
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [customUnit, setCustomUnit] = useState('minutes');

  // Toggle a preset reminder
  const toggleReminder = (minutes) => {
    const newSelected = new Set(selected);
    if (newSelected.has(minutes)) {
      newSelected.delete(minutes);
    } else {
      newSelected.add(minutes);
    }
    setSelected(newSelected);
  };

  // Add custom reminder
  const addCustomReminder = () => {
    if (!customValue || isNaN(parseInt(customValue, 10))) return;
    
    const value = parseInt(customValue, 10);
    let minutes = value;
    
    switch (customUnit) {
      case 'hours':
        minutes = value * 60;
        break;
      case 'days':
        minutes = value * 1440;
        break;
      case 'weeks':
        minutes = value * 10080;
        break;
      default:
        minutes = value;
    }
    
    const newSelected = new Set(selected);
    newSelected.add(minutes);
    setSelected(newSelected);
    setCustomValue('');
    setShowCustom(false);
  };

  // Remove a reminder
  const removeReminder = (minutes) => {
    const newSelected = new Set(selected);
    newSelected.delete(minutes);
    setSelected(newSelected);
  };

  // Save and close
  const handleSave = () => {
    const times = Array.from(selected).sort((a, b) => b - a); // Sort descending
    onSave(times);
    onClose();
  };

  // Get selected times sorted for display
  const selectedTimesSorted = Array.from(selected).sort((a, b) => b - a);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-500" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Currently Selected */}
          {selectedTimesSorted.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                Active Reminders
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedTimesSorted.map((mins) => (
                  <div
                    key={mins}
                    className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm"
                  >
                    <Clock className="h-3 w-3" />
                    {formatReminderTime(mins)}
                    <button
                      onClick={() => removeReminder(mins)}
                      className="ml-1 hover:bg-blue-100 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preset Options */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">
              Quick Select
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {PRESET_REMINDERS.map((preset) => (
                <label
                  key={preset.value}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selected.has(preset.value)
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <Checkbox
                    checked={selected.has(preset.value)}
                    onCheckedChange={() => toggleReminder(preset.value)}
                  />
                  <span className="text-sm">{preset.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Custom Reminder */}
          {!showCustom ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowCustom(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Custom Reminder
            </Button>
          ) : (
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium text-slate-700">
                Custom Reminder
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  placeholder="Value"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  className="w-24"
                />
                <Select value={customUnit} onValueChange={setCustomUnit}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="weeks">Weeks</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={addCustomReminder}>Add</Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCustom(false)}
                className="text-slate-500"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Morning Of Option */}
          <div className="p-4 bg-amber-50 rounded-lg">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={selected.has(-1)} // Special value for "morning of"
                onCheckedChange={() => toggleReminder(-1)}
              />
              <div>
                <span className="font-medium text-amber-800">Morning of appointment</span>
                <p className="text-xs text-amber-600 mt-0.5">
                  Get a reminder at 8:00 AM on the day of your appointment
                </p>
              </div>
            </label>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Reminders
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReminderTimePicker;
